import { isElementClippedByScrolling } from '../is-element-clipped-by-scrolling';
import { isElementScrolledOutsideView } from '../is-element-scrolled-outside-view';

export interface Point {
	x: number;
	y: number;
}

export interface OverlayPoint extends Point {
	visibleArea: number;
	fitsInViewport: boolean;
}

export type HorizontalConnectionPos = 'start' | 'center' | 'end';
export type VerticalConnectionPos = 'top' | 'center' | 'bottom';

export interface OriginConnectionPosition {
	originX: HorizontalConnectionPos;
	originY: VerticalConnectionPos;
}

export interface OverlayConnectionPosition {
	overlayX: HorizontalConnectionPos;
	overlayY: VerticalConnectionPos;
}

export class ConnectionPositionPair {
	originX: HorizontalConnectionPos;
	originY: VerticalConnectionPos;
	overlayX: HorizontalConnectionPos;
	overlayY: VerticalConnectionPos;

	constructor(
		origin: OriginConnectionPosition,
		overlay: OverlayConnectionPosition,
		public offsetX?: number,
		public offsetY?: number
	) {
		this.originX = origin.originX;
		this.originY = origin.originY;
		this.overlayX = overlay.overlayX;
		this.overlayY = overlay.overlayY;
	}
}

export interface ScrollingVisibility {
	isOriginClipped: boolean;
	isOriginOutsideView: boolean;
	isOverlayClipped: boolean;
	isOverlayOutsideView: boolean;
}

export function calculateOverlayElementPosition(
	overlayRect: ClientRect,
	overlayPoint: Point,
	pos: ConnectionPositionPair
) {
	const verticalStyleProperty = pos.overlayY === 'bottom' ? 'bottom' : 'top';

	const y =
		verticalStyleProperty === 'top'
			? overlayPoint.y
			: document.documentElement.clientHeight -
			  (overlayPoint.y + overlayRect.height);

	const horizontalStyleProperty = pos.overlayX === 'end' ? 'right' : 'left';

	const x =
		horizontalStyleProperty === 'left'
			? overlayPoint.x
			: document.documentElement.clientWidth -
			  (overlayPoint.x + overlayRect.width);

	return {
		verticalStyleProperty,
		horizontalStyleProperty,
		x,
		y,
	};
}

function getOriginConnectionPoint(
	originRect: DOMRect,
	pos: ConnectionPositionPair
): Point {
	const originStartX = originRect.left;
	const originEndX = originRect.right;

	let x: number;
	if (pos.originX === 'center') {
		x = originStartX + originRect.width / 2;
	} else {
		x = pos.originX === 'start' ? originStartX : originEndX;
	}

	let y: number;
	if (pos.originY === 'center') {
		y = originRect.top + originRect.height / 2;
	} else {
		y = pos.originY === 'top' ? originRect.top : originRect.bottom;
	}

	return { x, y };
}

function getOverlayPoint(
	originPoint: Point,
	overlayRect: ClientRect,
	pos: ConnectionPositionPair,
	baseOffsetX: number,
	baseOffsetY: number
): OverlayPoint {
	const viewportSize = {
		width: window.innerWidth,
		height: window.innerHeight,
	};

	let overlayStartX: number;
	if (pos.overlayX === 'center') {
		overlayStartX = -overlayRect.width / 2;
	} else if (pos.overlayX === 'start') {
		overlayStartX = 0;
	} else {
		overlayStartX = -overlayRect.width;
	}

	let overlayStartY: number;
	if (pos.overlayY === 'center') {
		overlayStartY = -overlayRect.height / 2;
	} else {
		overlayStartY = pos.overlayY === 'top' ? 0 : -overlayRect.height;
	}

	const offsetX = pos.offsetX === undefined ? baseOffsetX : pos.offsetX;
	const offsetY = pos.offsetY === undefined ? baseOffsetY : pos.offsetY;

	const x = originPoint.x + overlayStartX + offsetX;
	const y = originPoint.y + overlayStartY + offsetY;

	const leftOverflow = 0 - x;
	const rightOverflow = x + overlayRect.width - viewportSize.width;
	const topOverflow = 0 - y;
	const bottomOverflow = y + overlayRect.height - viewportSize.height;

	const visibleWidth = subtractOverflows(
		overlayRect.width,
		leftOverflow,
		rightOverflow
	);
	const visibleHeight = subtractOverflows(
		overlayRect.height,
		topOverflow,
		bottomOverflow
	);

	const visibleArea = visibleWidth * visibleHeight;
	const fitsInViewport = overlayRect.width * overlayRect.height === visibleArea;

	return { x, y, fitsInViewport, visibleArea };
}

function subtractOverflows(length: number, ...overflows: number[]): number {
	return overflows.reduce((currentValue: number, currentOverflow: number) => {
		return currentValue - Math.max(currentOverflow, 0);
	}, length);
}

export function getScrollVisibility(
	overlay: HTMLElement,
	origin: HTMLElement,
	scrollables: HTMLElement[]
): ScrollingVisibility {
	const originBounds = origin.getBoundingClientRect();
	const overlayBounds = overlay.getBoundingClientRect();
	const scrollContainerBounds = scrollables.map((s) =>
		s.getBoundingClientRect()
	);

	return {
		isOriginClipped: isElementClippedByScrolling(
			originBounds,
			scrollContainerBounds
		),
		isOriginOutsideView: isElementScrolledOutsideView(
			originBounds,
			scrollContainerBounds
		),
		isOverlayClipped: isElementClippedByScrolling(
			overlayBounds,
			scrollContainerBounds
		),
		isOverlayOutsideView: isElementScrolledOutsideView(
			overlayBounds,
			scrollContainerBounds
		),
	};
}

export function calculateOverlayPoint(
	originRect: DOMRect,
	overlayRect: DOMRect,
	pos: ConnectionPositionPair,
	offsetX: number,
	offsetY: number
): OverlayPoint {
	const originPoint = getOriginConnectionPoint(originRect, pos);
	return getOverlayPoint(originPoint, overlayRect, pos, offsetX, offsetY);
}

export function calculatePosition(
	origin: DOMRect,
	overlay: DOMRect,
	preferredPositions: ConnectionPositionPair[],
	offsetX: number,
	offsetY: number
) {
	let fallbackPoint: OverlayPoint | undefined;
	let fallbackPosition: ConnectionPositionPair | undefined;

	for (const pos of preferredPositions) {
		const overlayPoint = calculateOverlayPoint(
			origin,
			overlay,
			pos,
			offsetX,
			offsetY
		);
		if (overlayPoint.fitsInViewport) {
			return {
				point: overlayPoint,
				position: pos,
				isFallback: false,
			};
		} else if (
			!fallbackPoint ||
			fallbackPoint.visibleArea < overlayPoint.visibleArea
		) {
			fallbackPoint = overlayPoint;
			fallbackPosition = pos;
		}
	}

	return {
		point: fallbackPoint,
		position: fallbackPosition,
		isFallback: true,
	};
}
